import { useEffect, useMemo, useState } from 'react';

import { COUNTRY_OPTIONS } from 'shared/config/countries';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { useUpdateMasterProjectMutation } from '../api/master.api';
import { CountryCode, MasterIdentityPatch, ProjectDetail } from '../api/types';

interface Props {
  project: ProjectDetail;
}

interface Draft {
  name: string;
  countryCode: CountryCode;
  pusherIdentification: string;
  devUrl: string;
}

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const { data } = error as { data: unknown };
    if (typeof data === 'string') return data;
  }
  return fallback;
};

const buildDraft = (project: ProjectDetail): Draft => ({
  name: project.name ?? '',
  countryCode: project.countryCode ?? 'CL',
  pusherIdentification: project.pusherIdentification ?? '',
  devUrl: project.devUrl ?? '',
});

const MasterIdentityCard = ({ project }: Props) => {
  const [updateMasterProject, { isLoading }] = useUpdateMasterProjectMutation();
  const [draft, setDraft] = useState<Draft>(() => buildDraft(project));
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setDraft(buildDraft(project));
  }, [project]);

  const patch = useMemo<MasterIdentityPatch>(() => {
    const next: MasterIdentityPatch = {};

    if (draft.name !== (project.name ?? '')) next.name = draft.name.trim();
    if (draft.countryCode !== (project.countryCode ?? 'CL')) next.countryCode = draft.countryCode;
    if (draft.pusherIdentification !== (project.pusherIdentification ?? '')) {
      next.pusherIdentification = draft.pusherIdentification.trim();
    }
    if (draft.devUrl !== (project.devUrl ?? '')) next.devUrl = draft.devUrl.trim();
    return next;
  }, [draft, project]);

  const isDirty = Object.keys(patch).length > 0;

  const handleSave = async () => {
    if (!isDirty) return;
    setError(null);

    try {
      await updateMasterProject({ id: project.id, patch }).unwrap();
      setSavedAt(Date.now());
    } catch (saveError) {
      setError(extractErrorMessage(saveError, 'No se pudo guardar la inmobiliaria.'));
    }
  };

  return (
    <section className="space-y-4 rounded-md border border-border p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold">Datos de la inmobiliaria</h2>
        </div>
        <div className="flex items-center gap-3">
          {savedAt && !isDirty && <span className="text-xs text-text-muted">Guardado</span>}
          <Button
            type="button"
            onClick={() => {
              void handleSave();
            }}
            disabled={!isDirty || isLoading}
          >
            {isLoading ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor={`master-name-${project.id}`}>Nombre</Label>
          <Input
            id={`master-name-${project.id}`}
            value={draft.name}
            onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor={`master-country-${project.id}`}>País</Label>
          <Select
            value={draft.countryCode}
            onValueChange={(value) => setDraft((prev) => ({ ...prev, countryCode: value as CountryCode }))}
          >
            <SelectTrigger id={`master-country-${project.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_OPTIONS.map((option) => (
                <SelectItem key={option.code} value={option.code}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label htmlFor={`master-pusher-${project.id}`}>Pusher identification</Label>
          <Input
            id={`master-pusher-${project.id}`}
            value={draft.pusherIdentification}
            onChange={(event) => {
              setDraft((prev) => ({ ...prev, pusherIdentification: event.target.value }));
            }}
          />
        </div>

        <div className="space-y-1 md:col-span-2">
          <Label htmlFor={`master-dev-url-${project.id}`}>devUrl</Label>
          <Input
            id={`master-dev-url-${project.id}`}
            value={draft.devUrl}
            onChange={(event) => setDraft((prev) => ({ ...prev, devUrl: event.target.value }))}
            placeholder="https://..."
          />
          <p className="text-xs text-text-muted">
            Se usa a nivel master para el forwarding del inbound asociado al número de la inmobiliaria.
          </p>
        </div>
      </div>

      {error && (
        <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
      )}
    </section>
  );
};

export default MasterIdentityCard;
